/*
 * The live one-tap Orders card (owner decision 2026-10-23). Pure: no React, no network.
 *
 * BlinkScribe (order_suggest_v2/live.py) pushes {type: "live_orders", enabled, appointmentid, patientid, rows,
 * placed, gone} while the recording runs and keeps the same block in the v2 payload (payload.live_orders) for after
 * the stop. A row: { concept, order_name, variants, order_type, source: "voice" | "heard" | "chart" | "routine", reason,
 * quote, first_ts, seq }. "routine" (hubfix-oi-1026, BlinkScribe routine.py): a standing order the patient's history
 * makes likely (B12 every ~4 wk, flu season, ...) with its confidence p - shown low-key and after the other rows.
 *
 * What the card shows for a row (cardRows):
 *   open      an Order button (the visit's order is not on the chart yet)
 *   ordered   "Ordered ✓" (+ Undo for LIVE_UNDO_MS after the tap), placed by this card (record.liveOrdered)
 *   (hidden)  dismissed by the provider (record.liveDismissed), or already on the visit's orders by hand: by concept
 *             (BlinkScribe's placed list) or by order name / variant key (orderKey, the visit's orders/get)
 * The choices live in the visit's review record (orderReviewModel: liveOrdered / liveDismissed / liveShown), so the
 * end-of-visit review shows only the leftovers.
 */
import { FALLBACK_ORDER_ALIASES, orderKey } from "../../landingscreen/patientcharts/tabs/careiq/N3/orderKey";
import { isLiveClinicalOrder } from "./orderReviewModel";
import { normalizeApptId } from "../../utils/recordingCompletion";
import { defaultWhen, whenFor } from "./orderWhen";

export const LIVE_UNDO_MS = 120000;
export const LIVE_NEW_MS = 6000;           // a row seen this recently is highlighted
export const LIVE_POLL_MS = 20000;         // the visit's orders re-read while the card has open rows
export const SOURCE_LABEL = { voice: "Said", heard: "Discussed", chart: "Scheduled", routine: "Routine" };

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// The pushed / stored block when it belongs to this chart's visit, else null.
export const liveStateFor = (lo, patientId, apptKey) => {
  if (!lo || typeof lo !== "object" || lo.enabled !== true || !Array.isArray(lo.rows)) return null;
  if (patientId != null && lo.patientid != null && !sameId(lo.patientid, patientId)) return null;
  if (apptKey != null && lo.appointmentid != null && !sameId(lo.appointmentid, apptKey)) return null;
  return lo;
};

/*
 * LiveScribePanel: the live card replaces the voice panel only for THIS chart's visit (review 10-23: the patient AND
 * the appointment must match; a chart open on another visit of the same patient keeps the voice panel).
 */
export const liveCardOnFor = (lo, patientId, chart) => {
  if (!lo || lo.enabled !== true) return false;
  if (!sameId(lo.patientid, patientId)) return false;
  const appt = normalizeApptId(chart && chart.appointmentid);
  return Boolean(appt) && normalizeApptId(lo.appointmentid) === appt;
};

/*
 * The newer of two blocks of one visit: a = the socket's (always the latest recording: RecordingContext resets it
 * when a recording starts), b = the stored payload's (BlinkScribe's GET after a stop - possibly an EARLIER
 * recording's final rows). Review 10-23: a second recording on the same visit restarts BlinkScribe's version, so
 *   - while recording (preferSocket) the socket wins;
 *   - two different sessions (live.py session id; a block without one = an older build): the socket wins;
 *   - the same session: the higher version, then the later updated_at.
 */
export const newerLive = (a, b, { preferSocket = false } = {}) => {
  if (!a) return b || null;
  if (!b) return a;
  if (preferSocket) return a;
  if (String(a.session || "") !== String(b.session || "")) return a;
  const va = Number(a.version) || 0;
  const vb = Number(b.version) || 0;
  if (va !== vb) return vb > va ? b : a;
  return String(b.updated_at || "") >= String(a.updated_at || "") ? b : a;
};

export const rowKeys = (row, aliases = FALLBACK_ORDER_ALIASES) => {
  const out = new Set();
  [row && row.order_name, ...((row && Array.isArray(row.variants)) ? row.variants : [])].forEach((n) => {
    const k = orderKey(n, aliases);
    if (k) out.add(k);
  });
  return out;
};

export const visitOrderKeys = (orders, aliases = FALLBACK_ORDER_ALIASES) => {
  const keys = new Set();
  const ids = new Set();
  (Array.isArray(orders) ? orders : []).forEach((o) => {
    if (!isLiveClinicalOrder(o)) return;
    const k = orderKey(o.ordername || o.description || o.name || "", aliases);
    if (k) keys.add(k);
    if (o.id != null) ids.add(String(o.id));
  });
  return { keys, ids };
};

/*
 * The card's rows: [{ ...row, key, status: "open" | "ordered", canUndo, orderId, when, whenDefault }] in BlinkScribe's
 * order (seq). when = the mode the row is (or was) placed with: Today / Schedule (orderWhen.js; the provider's tap in
 * rec.when over BlinkScribe's default); whenDefault = BlinkScribe's default (monitoring).
 * rec = the visit's review record; visitOrders = orders/get of the visit (null = not read yet); now = Date.now().
 */
export const cardRows = (lo, { rec = null, visitOrders = null, aliases = FALLBACK_ORDER_ALIASES, now = Date.now() } = {}) => {
  if (!lo || !Array.isArray(lo.rows)) return [];
  const dismissed = (rec && rec.liveDismissed) || {};
  const ordered = (rec && rec.liveOrdered) || {};
  const placed = new Set(Array.isArray(lo.placed) ? lo.placed : []);
  const vo = visitOrders ? visitOrderKeys(visitOrders, aliases) : null;
  const out = [];
  const seen = new Set();
  [...lo.rows].sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0)).forEach((r) => {
    if (!r || !r.concept || !r.order_name || seen.has(r.concept)) return;
    seen.add(r.concept);
    if (dismissed[r.concept]) return;
    const keys = rowKeys(r, aliases);
    const key = orderKey(r.order_name, aliases);
    const mine = ordered[r.concept];
    if (mine) {
      // placed by this card: shown as ordered while the order exists (deleted elsewhere = offer it again)
      const gone = vo && mine.order_id != null && !vo.ids.has(String(mine.order_id)) && !(keys.size && [...keys].some((k) => vo.keys.has(k)));
      if (!gone) {
        out.push({
          ...r, key, status: "ordered", orderId: mine.order_id, canUndo: now - (Number(mine.at) || 0) < LIVE_UNDO_MS,
          when: mine.when || whenFor(rec, r), whenDefault: defaultWhen(r),
        });
        return;
      }
    }
    if (placed.has(r.concept)) return;                                   // BlinkScribe saw it on today's orders
    if (vo && [...keys].some((k) => vo.keys.has(k))) return;              // placed by hand (by name)
    out.push({ ...r, key, status: "open", orderId: null, canUndo: false, when: whenFor(rec, r), whenDefault: defaultWhen(r) });
  });
  // Review 10-23: an order this card placed leaves BlinkScribe's rows once BlinkScribe sees it on today's orders -
  // it stays on the card (from the record) while Undo is open, unless the order is gone from the visit
  Object.keys(ordered).forEach((concept) => {
    const mine = ordered[concept];
    if (seen.has(concept) || dismissed[concept] || !mine || !mine.name) return;
    if (now - (Number(mine.at) || 0) >= LIVE_UNDO_MS) return;
    const r = { concept, order_name: mine.name, variants: [mine.name], source: mine.source || null, reason: "" };
    const keys = rowKeys(r, aliases);
    const gone = vo && mine.order_id != null && !vo.ids.has(String(mine.order_id)) && !(keys.size && [...keys].some((k) => vo.keys.has(k)));
    if (gone) return;
    seen.add(concept);
    out.push({
      ...r, key: orderKey(mine.name, aliases), status: "ordered", orderId: mine.order_id, canUndo: true, fromRecord: true,
      when: mine.when || "today", whenDefault: null,
    });
  });
  return out;
};

export const openCount = (rows) => rows.filter((r) => r.status === "open").length;

// ms the row has been on screen (BlinkScribe's first_ts), or null.
export const rowAge = (row, now = Date.now()) => {
  const t = row && row.first_ts ? Date.parse(row.first_ts) : NaN;
  return Number.isFinite(t) ? Math.max(0, now - t) : null;
};

export const recordOrdered = (rec, row, orderId, ordertype, now = Date.now()) => ({
  ...rec,
  liveOrdered: {
    ...((rec && rec.liveOrdered) || {}),
    [row.concept]: { order_id: orderId, at: now, name: row.order_name, ordertype, source: row.source || null, when: row.when || "today" },
  },
});
// Cross-tab in-flight marker (re-review 10-23): written to the visit's record right before the POST, cleared after;
// a marker older than LIVE_INFLIGHT_MS is ignored (a tab closed mid-POST never blocks the row for good).
export const LIVE_INFLIGHT_MS = 30000;
export const recordInflight = (rec, concept, now = Date.now()) => ({
  ...rec, liveInflight: { ...((rec && rec.liveInflight) || {}), [concept]: now },
});
export const clearInflight = (rec, concept) => {
  const m = { ...((rec && rec.liveInflight) || {}) };
  delete m[concept];
  return { ...rec, liveInflight: m };
};
export const inflightIn = (rec, concept, now = Date.now()) => {
  const t = Number(rec && rec.liveInflight && rec.liveInflight[concept]) || 0;
  return t > 0 && now - t < LIVE_INFLIGHT_MS;
};
// Was this card's order (record.liveOrdered[concept]) still on the visit's orders? (by id, else by name key)
export const orderStillOnVisit = (mine, visitOrders, aliases = FALLBACK_ORDER_ALIASES) => {
  if (!mine || !Array.isArray(visitOrders)) return false;
  const vo = visitOrderKeys(visitOrders, aliases);
  if (mine.order_id != null && vo.ids.has(String(mine.order_id))) return true;
  const k = orderKey(mine.name || "", aliases);
  return Boolean(k) && vo.keys.has(k);
};
export const recordUndone = (rec, concept) => {
  const lo = { ...((rec && rec.liveOrdered) || {}) };
  delete lo[concept];
  return { ...rec, liveOrdered: lo };
};
export const recordDismissed = (rec, concept) => ({
  ...rec, liveDismissed: { ...((rec && rec.liveDismissed) || {}), [concept]: true },
});
// The concepts not yet logged as shown (each row is logged once per visit) and the record that remembers them.
export const markShown = (rec, rows) => {
  const shown = { ...((rec && rec.liveShown) || {}) };
  const fresh = rows.filter((r) => r.status === "open" && !shown[r.concept]);
  fresh.forEach((r) => { shown[r.concept] = 1; });
  return { fresh, rec: fresh.length ? { ...rec, liveShown: shown } : rec };
};
